import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { Grid, Row, Caption, LabelCaption, H4, Tag } from "@maggioli-design-system/react";
import { IBook } from "../Interfaces/IBook";
import * as Months from "../lib/Months";
import { IUserSubscription } from "../Interfaces/IUserSubscription";
import { IProdotto } from "../Interfaces/IProdotto";
import BookCover from "./BookCover";
import AIBadge from "./AIBadge";
import { getCookie } from "../lib/Constants";
import { useNavigate } from "react-router-dom";
import { asButton } from "../lib/A11y";

interface IWhishlist {
    hasAttach: boolean;
    update: Function;
    allFavorites: boolean;
    user: IUserSubscription;
    data: IBook;
}

interface IWhishlistItem {
    id: IBook;
    hasAttach: boolean;
}

const Whishlist = ({ data, user, allFavorites, update, hasAttach }: IWhishlist): JSX.Element => {
    const isLogged: boolean = getCookie("refresh_token") !== null;
    const [added, setAdded] = useState<boolean>(false);

    useEffect(() => {
        const whishlist: IWhishlistItem[] = JSON.parse(localStorage.getItem("whishlist"));
        if (whishlist && whishlist.filter((elem: IWhishlistItem) => elem.id.isbn === data.isbn).length > 0) {
            setAdded(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data]);

    const addToWhislist = (data: IBook) => {
        const whishlist: IWhishlistItem[] = JSON.parse(localStorage.getItem("whishlist"));
        if (!whishlist) {
            const object: IWhishlistItem[] = [
                {
                    id: data,
                    hasAttach: hasAttach,
                },
            ];
            localStorage.setItem("whishlist", JSON.stringify(object));
        } else {
            whishlist.push({ id: data, hasAttach: hasAttach });
            localStorage.setItem("whishlist", JSON.stringify(whishlist));
        }

        setAdded(true);
    };

    const removeToWhislist = (data: IBook) => {
        const whishlist: IWhishlistItem[] = JSON.parse(localStorage.getItem("whishlist"));
        const removed: IWhishlistItem[] = whishlist.filter((elem: IWhishlistItem) => elem.id.isbn !== data.isbn);
        localStorage.setItem("whishlist", JSON.stringify(removed));
        if (typeof update === "function") {
            update();
        }
        setAdded(false);
    };

    if (!isLogged) {
        return null;
    }

    // Tasto aggiungi/rimuovi dai preferiti
    return added || allFavorites ? (
        <Tag icon="data-favorite" className="cursor-pointer px-0 text-status-error-05"
            {...asButton(() => removeToWhislist(data), `Rimuovi ${data.name} dai preferiti`)}>
            <Caption className="color-adjust-tone-05">Rimuovi dai preferiti</Caption>
        </Tag>
    ) : (
        <Tag icon="data-unfavorite" className="cursor-pointer px-0"
            {...asButton(() => addToWhislist(data), `Aggiungi ${data.name} ai preferiti`)}>
            <Caption className="color-adjust-tone-04">Aggiungi ai preferiti</Caption>
        </Tag>
    );
};

interface CustomBook {
    update: Function;
    setShow: Function;
    setIsbn: Function;
    setShowModal: Function;
    setAttachModal: Function;
    setViewer?: Function;
    user?: IUserSubscription;
    data: IBook;
    hasAttach: boolean;
    iLibro: boolean;
    allFavorites: boolean;
    actions: boolean;
    idProdotti?: IProdotto[];
}

// Label novità
export const BookLabel = ({ className, ...restProps }) => (
    <LabelCaption
        htmlTag="div"
        className={`text-adjust-tone inline-flex px-1.5 py-0 justify-center rounded border border-solid border-adjust-tone ${ className || "" }`}
        {...restProps}
    >
        { restProps.children }
    </LabelCaption>
);

const Book = ({
    setShow,
    setIsbn,
    setShowModal,
    setViewer,
    user,
    data,
    hasAttach,
    setAttachModal,
    iLibro,
    update,
    allFavorites,
    actions,
}: CustomBook): JSX.Element => {
    
    const history = useNavigate();

    const open = (isbn: string) => {
        if (!data.hasAccessToExtraContent) {
            setShowModal(true);
        } else {
            setViewer && setViewer(data.viewer);
            setIsbn(isbn);
            setShow(true);
        }
    };

    const onClickAttachments = () => {
        if (!data.hasAccessToExtraContent) {
            setShowModal(true);
        } else {
            setAttachModal({ book: data, visible: true });
        }
    };

    const onClickLibro = () => {
        sessionStorage.setItem("scrollPage", window.scrollY.toString());
        sessionStorage.setItem("book", JSON.stringify(data));
        sessionStorage.setItem("book-hasAttach", hasAttach.toString());
    };

    const bookSlug: string = `${data.name.replaceAll(" ", "-").replaceAll("/", "-").replaceAll(",", "-")}-${data.isbn}`;
    const btnIcon: string = iLibro ? "logo-ilibro" : "";
    const isNew = data.mese >= new Date().getMonth() && data.anno === new Date().getFullYear();
    const iaButton = <Tag chip className="cursor-pointer hover:background-color-brand-maggioli-18"
        {...asButton(() => history(`/dettaglio/${bookSlug}`), `Funzionalità AI del volume ${data.name}`)}>
        <div aria-hidden="true" className=" flex justify-center gap-2">
            <AIBadge className="h-[22px]"/>
            <Caption>Funzionalità AI</Caption>
        </div>
    </Tag>

    return (
        <Grid gutter="small" rows="fit-vertically">
            <Row gutter="small" className="items-start">
                {/* Immagine del libro */}
                <Link className="min-w-8 relative overflow-visible rounded-md" to={`/dettaglio/${bookSlug}`} onClick={onClickLibro}>
                    { isNew && <BookLabel className="bg-label-green-06 absolute top-2 right-2 z-[9]" >Novità</BookLabel> }
                    {/* <BookLabel className="bg-adjust-tone-06 absolute bottom-2 right-2 z-10">Funzioni AI</BookLabel> */}
                    <BookCover data={data} />
                </Link>

                {/* Info libro */}
                <Grid gutter="xsmall" className="py-3">
                    <Link key={data.isbn} to={`/dettaglio/${bookSlug}`} onClick={onClickLibro}>
                        <H4 className="text-xl">{data.name}</H4>
                    </Link>
                    <Grid gutter="xxsmall">
                        <Caption>
                            <strong>Autori:</strong>{" "}
                            {data.autori.join(", ").length > 30 ? `${data.autori.join(", ").slice(0, 30)}...` : data.autori.join(", ")}
                        </Caption>
                        {/* <Caption>
                            <strong>Area:</strong>{" "}
                            {data.categoria.map((c, i) => {
                                const includedAreas = areas.filter(a => a.areaLabel.includes(c))[0];
                                if (includedAreas === undefined) {
                                    return;
                                }
                                return <span key={i} className={includedAreas.color}>{c} </span>
                            })}
                        </Caption> */}
                        <Caption>{`${Months.getName(data.mese)} ${data.anno}`}</Caption>
                    </Grid>
                    <Whishlist hasAttach={hasAttach} update={update} data={data} user={user} allFavorites={allFavorites} />
                </Grid>
            </Row>

            {/* Bottoni iLibro e allegati */}
            {actions && (
                <>
                    <Row className="-mb-3">
                        <Tag icon={btnIcon} chip className="cursor-pointer hover:background-color-brand-maggioli-18"
                            {...asButton(() => open(data.isbn), `Consulta il volume ${data.name}`)}>
                            <Caption>Consulta il volume</Caption>
                        </Tag>
                        {hasAttach ? (
                            <Tag
                                icon="additional-contents"
                                chip
                                className="cursor-pointer hover:background-color-brand-maggioli-18"
                                {...asButton(() => onClickAttachments(), `Contenuti aggiuntivi del volume ${data.name}`)}
                            >
                                <Caption>Contenuti aggiuntivi</Caption>
                            </Tag>
                        ) :  data.aipenDocumentiId !== undefined ? iaButton : <></> }
                    </Row>
                    { hasAttach && data.aipenDocumentiId !== undefined && <Row>
                        {iaButton}
                    </Row> }
                </>
            )}
        </Grid>
    );
};

export default Book;
